import React from "react"

import type { NumberInputFieldProps, NumberInputProps } from "@chakra-ui/react"
import {
  forwardRef,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
} from "@chakra-ui/react"

import { formatCurrency, parseCurrency } from "@supliful/domain"

import type { EditorProps } from "./Editor"

type NumberEditorProps = {
  format?: (value: number) => string
  parse?: (value: string) => number
  dataTestId?: string
  inputProps?: NumberInputFieldProps
} & EditorProps<number> &
  Omit<NumberInputProps, "parse" | "format">

export const NumberEditor = forwardRef(
  (
    {
      value,
      isValueInvalid,
      onValueChange,
      format,
      parse,
      dataTestId,
      inputProps,
      ...props
    }: NumberEditorProps,
    ref,
  ) => {
    const formatFunction: (value: number) => string = format || formatCurrency
    const parseFunction: (value: string) => number = parse || parseCurrency

    return (
      <NumberInput
        defaultValue={formatFunction(value)}
        isInvalid={isValueInvalid}
        min={0}
        precision={2}
        step={1}
        onBlur={(event) => {
          if (onValueChange) {
            onValueChange(parseFunction(event.currentTarget.value))
          }
        }}
        focusBorderColor="main.700"
        {...props}
        data-testid={dataTestId ? dataTestId : ""}
      >
        <NumberInputField
          ref={ref}
          minWidth={16}
          pl={{ base: 2, md: 4 }}
          fontSize={{ base: 12, sm: 14 }}
          {...inputProps}
        />

        <NumberInputStepper>
          <NumberIncrementStepper />
          <NumberDecrementStepper />
        </NumberInputStepper>
      </NumberInput>
    )
  },
)
